import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useTeam } from '@/contexts/TeamContext';
import {
  Lightning, CheckCircle, XCircle, SpinnerGap, Clock, ArrowRight, Notepad
} from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Pending Approval', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-600 border-red-200', icon: XCircle },
};

function TaskApproval({ task, onUpdate }) {
  const { activeTeam } = useTeam();
  const [submitting, setSubmitting] = useState(false);
  const [showRejectNotes, setShowRejectNotes] = useState(false);
  const [notes, setNotes] = useState('');

  if (!activeTeam) return null;

  const teamId = activeTeam.team_id;
  const myRole = activeTeam.my_role;
  const canReview = myRole === 'owner' || myRole === 'admin';
  const approvalStatus = task.approval_status;
  const statusConfig = STATUS_CONFIG[approvalStatus];

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/teams/${teamId}/tasks/${task.task_id}/submit-approval`, {}, { withCredentials: true });
      if (onUpdate) onUpdate();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/teams/${teamId}/tasks/${task.task_id}/approve`,
        { notes: notes || null }, { withCredentials: true });
      if (onUpdate) onUpdate();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/teams/${teamId}/tasks/${task.task_id}/reject`,
        { notes: notes || null }, { withCredentials: true });
      setShowRejectNotes(false);
      setNotes('');
      if (onUpdate) onUpdate();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 mb-4" data-testid="task-approval-section">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
        <Lightning size={13} weight="bold" />
        Approval
      </h4>

      {/* Current status badge */}
      {statusConfig && (
        <div className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium border mb-3 ${statusConfig.cls}`}
          data-testid="approval-status-badge">
          <statusConfig.icon size={15} weight="fill" />
          {statusConfig.label}
        </div>
      )}

      {/* Approval notes if any */}
      {task.approval_notes && (approvalStatus === 'approved' || approvalStatus === 'rejected') && (
        <div className="bg-slate-50 rounded-lg p-3 mb-3 flex items-start gap-2">
          <Notepad size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-slate-500">Review Notes</p>
            <p className="text-sm text-slate-700">{task.approval_notes}</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {/* Submit for approval (any member, only if not already pending) */}
        {!approvalStatus && (
          <button onClick={handleSubmit} disabled={submitting}
            className="bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 font-medium px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-colors"
            data-testid="submit-approval-btn">
            {submitting ? <SpinnerGap size={13} className="animate-spin" /> : <ArrowRight size={13} weight="bold" />}
            Submit for Approval
          </button>
        )}

        {/* Re-submit if rejected */}
        {approvalStatus === 'rejected' && (
          <button onClick={handleSubmit} disabled={submitting}
            className="bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 font-medium px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-colors"
            data-testid="resubmit-approval-btn">
            {submitting ? <SpinnerGap size={13} className="animate-spin" /> : <ArrowRight size={13} weight="bold" />}
            Re-submit for Approval
          </button>
        )}

        {/* Approve/Reject (owner/admin only, only if pending) */}
        {approvalStatus === 'pending' && canReview && (
          <>
            <button onClick={handleApprove} disabled={submitting}
              className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-medium px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-colors"
              data-testid="approve-btn">
              {submitting ? <SpinnerGap size={13} className="animate-spin" /> : <CheckCircle size={13} weight="bold" />}
              Approve
            </button>
            <button onClick={() => setShowRejectNotes(true)} disabled={submitting}
              className="bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 font-medium px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-colors"
              data-testid="reject-btn">
              <XCircle size={13} weight="bold" />
              Reject
            </button>
          </>
        )}
      </div>

      {/* Reject notes input */}
      <AnimatePresence>
        {showRejectNotes && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="mt-3 overflow-hidden">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for rejection (optional)..."
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-navy focus:ring-2 focus:ring-blue-100 resize-none"
              rows={2} data-testid="reject-notes-input" />
            <div className="flex gap-2 mt-2">
              <button onClick={handleReject} disabled={submitting}
                className="bg-red-600 text-white hover:bg-red-700 font-medium px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-colors"
                data-testid="confirm-reject-btn">
                {submitting ? <SpinnerGap size={13} className="animate-spin" /> : <XCircle size={13} weight="bold" />}
                Confirm Rejection
              </button>
              <button onClick={() => { setShowRejectNotes(false); setNotes(''); }}
                className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default TaskApproval;
